import React, { useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import { useConfirm } from '../../components/shared/Confirm';
import { useAuth } from '../../context/AuthContext';

const ROLE_LABEL = { barbero: 'Barbero', admin: 'Administrador', admin_general: 'Admin General' };

function WorkerAvatar({ worker, size = 28 }) {
  return worker.url_foto
    ? <img src={worker.url_foto} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
    : <div style={{ width: size, height: size, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.5, flexShrink: 0 }}>✂️</div>;
}

function WorkerMultiSelect({ workers, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = id => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  const selectedWorkers = workers.filter(w => selected.includes(w.id));

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6,
          minHeight: 42, padding: '6px 36px 6px 10px', border: '1px solid var(--color-border)',
          borderRadius: 8, background: '#fff', cursor: 'pointer', position: 'relative',
        }}
      >
        {selectedWorkers.length === 0
          ? <span style={{ color: '#94a3b8', fontSize: '0.88rem' }}>Seleccionar trabajadores...</span>
          : selectedWorkers.map(w => (
            <span key={w.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 20,
              padding: '2px 8px 2px 4px', fontSize: '0.8rem', color: '#1d4ed8',
            }}>
              <WorkerAvatar worker={w} size={18} />
              {w.nombre || w.name}
              <span
                onClick={e => { e.stopPropagation(); toggle(w.id); }}
                style={{ marginLeft: 2, cursor: 'pointer', color: '#6b7280', fontWeight: 700, lineHeight: 1 }}
              >×</span>
            </span>
          ))
        }
        <span style={{ position: 'absolute', right: 10, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, transition: 'transform 0.2s', color: '#94a3b8', fontSize: 12 }}>▼</span>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 100,
          background: '#fff', border: '1px solid var(--color-border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', maxHeight: 240, overflowY: 'auto',
        }}>
          {workers.map((w, i) => {
            const checked = selected.includes(w.id);
            return (
              <div
                key={w.id}
                onClick={() => toggle(w.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  cursor: 'pointer', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none',
                  background: checked ? '#eff6ff' : '#fff',
                  transition: 'background 0.1s',
                }}
              >
                <input type="checkbox" checked={checked} readOnly
                  style={{ width: 15, height: 15, accentColor: '#2563eb', flexShrink: 0 }} />
                <WorkerAvatar worker={w} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f172a' }}>{w.nombre || w.name}</div>
                  <div style={{ fontSize: '0.73rem', color: '#94a3b8' }}>{ROLE_LABEL[w.role || w.rol] || ''}</div>
                </div>
                {checked && <span style={{ color: '#2563eb', fontSize: 14, fontWeight: 700 }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const EMPTY = { name: '', address: '', phone: '' };

export default function BranchesPage() {
  const { hasPlan, user } = useAuth();
  const canManage = user?.role === 'admin_general' || user?.role === 'admin' || user?.role === 'super_admin';
  const [branches, setBranches] = useState([]);
  const [workers, setWorkers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  // Antes del return temprano por plan: los hooks no pueden quedar detras de el.
  const confirmar = useConfirm();

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/branches'), api.get('/workers')])
      .then(([b, w]) => { setBranches(b.data); setWorkers(w.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (!hasPlan('has_multi_branch')) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🏪</div>
        <h3>Modulo no disponible</h3>
        <p>La gestion de multiples sucursales esta disponible en el <strong>Plan Premium</strong>.</p>
      </div>
    );
  }

  const openCreate = () => {
    setForm(EMPTY);
    setSelectedWorkers([]);
    setModal('create');
    setError('');
  };

  const openEdit = b => {
    setForm({ name: b.nombre || '', address: b.direccion || '', phone: b.telefono || '' });
    const assigned = workers.filter(w => String(w.sucursal_id) === String(b.id)).map(w => w.id);
    setSelectedWorkers(assigned);
    setModal(b);
    setError('');
  };

  const deleteBranch = async b => {
    if (!await confirmar({
      titulo: 'Eliminar sucursal',
      mensaje: `¿Eliminar la sucursal "${b.nombre}"?`,
      detalle: 'Esta acción no se puede deshacer.',
      aceptar: 'Eliminar',
      peligro: true,
    })) return;
    try {
      await api.delete(`/branches/${b.id}`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const toggleWorker = id => {
    setSelectedWorkers(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      let branchId = modal === 'create' ? null : modal.id;

      if (modal === 'create') {
        const res = await api.post('/branches', form);
        branchId = res.data.id;
      } else {
        await api.put(`/branches/${modal.id}`, form);
      }

      // Asignar trabajadores seleccionados a esta sucursal
      await api.patch(`/branches/${branchId}/workers`, { worker_ids: selectedWorkers });

      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const toggle = async id => { await api.patch(`/branches/${id}/toggle`); load(); };

  const activeWorkers = workers.filter(w => w.activo || w.is_active !== false);

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Sucursales</h2>
          <p>Gestiona las sedes de tu barberia</p>
        </div>
        {canManage && <button className="btn btn-primary" onClick={openCreate}>+ Nueva Sucursal</button>}
      </div>

      {error && !modal && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : branches.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏪</div>
          <h3>Sin sucursales</h3>
          <p>Crea tu primera sede para comenzar</p>
          {canManage && <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openCreate}>+ Crear sucursal</button>}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {branches.map(b => {
            const branchWorkers = workers.filter(w => String(w.sucursal_id) === String(b.id));
            return (
              <div key={b.id} className="card">
                <div style={{ padding: '1.25rem 1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 2 }}>{b.nombre}</div>
                      <span className={`badge badge-${b.activo ? 'attended' : 'cancelled'}`}>
                        {b.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                    <div style={{ fontSize: '1.5rem' }}>🏪</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: 12 }}>
                    {b.direccion && <span>📍 {b.direccion}</span>}
                    {b.telefono && <span>📞 {b.telefono}</span>}
                  </div>
                  {branchWorkers.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Trabajadores ({branchWorkers.length})
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {branchWorkers.map(w => (
                          <span key={w.id} style={{ fontSize: '0.78rem', background: 'var(--color-surface-2, #f1f5f9)', padding: '2px 8px', borderRadius: 20, color: 'var(--color-text)' }}>
                            {w.nombre || w.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {canManage && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(b)}>Editar</button>
                      <button className={`btn btn-sm ${b.activo ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggle(b.id)}>
                        {b.activo ? 'Desactivar' : 'Activar'}
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => deleteBranch(b)} title="Eliminar sucursal">
                        🗑 Eliminar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'Nueva Sucursal' : 'Editar Sucursal'}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

                <div className="form-group">
                  <label className="form-label">Nombre de la sede *</label>
                  <input className="form-input" required value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Ej: Sede Centro" />
                </div>
                <div className="form-group">
                  <label className="form-label">Dirección</label>
                  <input className="form-input" value={form.address}
                    onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input className="form-input" value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </div>

                {activeWorkers.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Asignar trabajadores</label>
                    <WorkerMultiSelect
                      workers={activeWorkers}
                      selected={selectedWorkers}
                      onChange={setSelectedWorkers}
                    />
                    {selectedWorkers.length > 0 && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 5 }}>
                        {selectedWorkers.length} seleccionado{selectedWorkers.length > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
